import { useMemo } from 'react';
import { PERMISSIONS, ROLES, ROLES_PERMISSIONS } from '@/constants';
import { useEnforceAccessControl } from '@/hooks';
import { useAuthStore } from '@/store/useAuthStore';
import { isOwnSelfOnlyProfile } from '@/utils/authUtils';

// What an employee with taken-away permissions still sees on their own
// profile: the tiles below exist only while the permission they rest on is
// kept; every other tile has no permission of its own and goes.
const OWN_RESTRICTED_TILES = ['licno', 'isplata', 'zarada'];

// Tiles resting on routes that stay closed for a suspended employee.
const SUSPENDED_HIDDEN_TILES = ['vreme', 'go', 'odsustva', 'dok', 'oprema'];

const MANAGER_ROLES = [
  ROLES.superAdmin,
  ROLES.techAdmin,
  ROLES.admin,
  ROLES.client,
  ROLES.clientOfficer,
  ROLES.hrOfficer,
];

/**
 * Who may see and do what on the employee profile — the same rules the old
 * view and edit pages applied.
 */
export default function useEmployeeProfileAccess({
  employeeId,
  isHolidayTracker,
  isSuspended = false,
}) {
  const user = useAuthStore((state) => state.user);
  const roles = useMemo(() => user?.roles || [], [user?.roles]);
  const hasEditRight = useEnforceAccessControl(
    ROLES_PERMISSIONS.EMPLOYEES.EDIT
  );

  return useMemo(() => {
    const isViewingSelf = String(user?.employee) === String(employeeId);
    const hrSectorIds = (user?.hrSectorIds || []).map(String);
    const isHrViewingSelfOutsideSector =
      roles.includes(ROLES.hrOfficer) &&
      isViewingSelf &&
      !hrSectorIds.includes(String(user?.employeeSector || ''));
    const isManager = roles.some((r) => MANAGER_ROLES.includes(r));
    // An accountant viewing their own HLB TM profile: an employee's view of it.
    const isSelfOnly = isOwnSelfOnlyProfile(user, employeeId);

    // Taken-away permissions (`restrictedPermissions`) hide what rests on
    // them. Accounts without any keep exactly what they always had, even where
    // their role itself lacks one of these permissions.
    const isRestricted = !!user?.hasRestrictedPermissions;
    const permissions = user?.permissions || [];
    const keeps = (permission) =>
      !isRestricted || permissions.includes(permission);
    const canSeeRequests = keeps(PERMISSIONS.hrRequestView);
    const canSeeTimesheet =
      keeps(PERMISSIONS.time_sheetView) && keeps(PERMISSIONS.timeTrackingView);
    const isOwnRestricted = isRestricted && isViewingSelf;
    const canCreateRequest = keeps(PERMISSIONS.hrRequestCreate);

    const access = {
      // Own profile of an account with taken-away permissions: a short list.
      isOwnRestricted,
      canCreateRequest,
      // HR documents, leave tiles and the absence calendar are request data.
      canSeeRequests,
      canSeeTimesheet,
      /** Whether a tile may be shown at all, before its own conditions. */
      allowTile: (id) => {
        if (['go', 'odsustva', 'dok'].includes(id) && !canSeeRequests) {
          return false;
        }
        if (id === 'vreme' && !canSeeTimesheet) return false;
        if (!isOwnRestricted) return true;
        return (
          OWN_RESTRICTED_TILES.includes(id) ||
          (id === 'vreme' && canSeeTimesheet) ||
          (['go', 'odsustva'].includes(id) && canSeeRequests) ||
          // Own equipment requests rest on `request_create`.
          (id === 'oprema' && canCreateRequest)
        );
      },
      canEdit: hasEditRight && !isHrViewingSelfOutsideSector && !isSelfOnly,
      // Same roles the server lets set the photo (PUT /employees/:id/photo).
      canEditPhoto:
        hasEditRight &&
        isManager &&
        !isHrViewingSelfOutsideSector &&
        !isSelfOnly,
      canSeeContracts:
        isManager && !isHrViewingSelfOutsideSector && !isSelfOnly,
      // Extend / terminate / bulk actions — not on one's own self-only profile.
      canManageEmployment: !isSelfOnly,
      canSeeLeaveHistory:
        isHolidayTracker &&
        !isHrViewingSelfOutsideSector &&
        (isManager || isViewingSelf),
      // Notice to colleagues and equipment request: managers, on someone
      // else's profile (the server checks the company and the sector).
      canOnboard: isManager && !isViewingSelf && !isSelfOnly,
      // The equipment tile on someone else's profile: also a head of their
      // sector holding only the employee role (to approve an own request);
      // the server refuses everyone else, which leaves the tile out.
      canSeeEquipment: !isViewingSelf && !isSelfOnly,
      // ZAZ: never on one's own profile; the server decides who else (HR of
      // the company, the heads of the employee's sector).
      canSeeZaz: !isViewingSelf && !isSelfOnly,
      // Own profile: own equipment requests, while `request_create` is kept.
      canRequestOwnEquipment: isViewingSelf && canCreateRequest,
      isTechAdmin: roles.includes(ROLES.techAdmin),
      // Role-only gate, like the /oz route itself (OZ.VIEW lists no permissions).
      canSeeOz:
        !isSelfOnly &&
        roles.some((r) => ROLES_PERMISSIONS.OZ.VIEW.requiredRoles.includes(r)),
    };

    // The server opens a suspended employee for reading and for the restore
    // only, so nothing that would be refused is offered.
    if (isSuspended) {
      return {
        ...access,
        allowTile: (id) =>
          !SUSPENDED_HIDDEN_TILES.includes(id) && access.allowTile(id),
        canSeeRequests: false,
        canSeeTimesheet: false,
        canCreateRequest: false,
        canEdit: false,
        canEditPhoto: false,
        canSeeContracts: false,
        canManageEmployment: false,
        canSeeLeaveHistory: false,
        canOnboard: false,
        canSeeEquipment: false,
        canSeeZaz: false,
        canRequestOwnEquipment: false,
      };
    }

    return access;
  }, [user, roles, employeeId, isHolidayTracker, hasEditRight, isSuspended]);
}
